import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SelectItem } from "@heroui/react";
import AppSelect from "../common/appSelect";
import {
  OutcomeRegistry,
  OutcomeRegistryRow,
  OutcomePolicyFormValue,
  PayoutComponent,
  getCurrencySymbol,
  getOutcomeRegistryRowsForUi,
  getOutcomeComponentAmount,
  getOutcomeTotal,
  getOutcomeTotalLabel,
  formatRegistryText,
} from "../../utils/opportunities/outcomeRegistryHelpers";

type Option = { label: string; value: string };
type SelectOption = Option & { disabled?: boolean };
type Translate = (key: string, fallback?: string) => string;

const LABEL_CLASS = "block text-xs font-medium text-[#71717A] mb-1.5";

type StyledSelectProps = {
  value: string;
  options: SelectOption[];
  placeholder: string;
  disabled?: boolean;
  onChange: (value: string) => void;
};

const StyledSelect: React.FC<StyledSelectProps> = (props) => {
  const { value, options, placeholder, disabled, onChange } = props;
  return (
    <AppSelect
      aria-label={placeholder}
      variant="bordered"
      className="w-full"
      placeholder={placeholder}
      isDisabled={disabled}
      disallowEmptySelection
      selectedKeys={value ? [value] : []}
      disabledKeys={options.filter((opt) => opt.disabled).map((opt) => opt.value)}
      onSelectionChange={(keys) => {
        const next = keys instanceof Set ? Array.from(keys)[0] : undefined;
        if (next !== undefined) onChange(String(next));
      }}
      classNames={{
        trigger:
          "h-10 min-h-10 rounded-2xl border-small border-[#E4E4E7] px-3.5 bg-white shadow-[0px_1px_2px_rgba(0,0,0,0.05)] data-[hover=true]:border-[#0080FF] data-[focus=true]:border-[#0080FF] data-[open=true]:border-[#0080FF]",
        value: "text-sm text-[#11181C]",
      }}
    >
      {options.map((opt) => (
        <SelectItem key={opt.value}>{opt.label}</SelectItem>
      ))}
    </AppSelect>
  );
};

type ComponentRowProps = {
  value: string;
  amount: number | null;
  currencySymbol: string;
  options: Option[];
  takenValues: string[];
  disabled: boolean;
  actionType: "add" | "remove";
  actionDisabled?: boolean;
  onChange: (value: string) => void;
  onAction: () => void;
};

const ComponentRow: React.FC<ComponentRowProps> = (props) => {
  const { value, amount, currencySymbol, options, takenValues, disabled, actionType, actionDisabled } = props;
  const { t } = useTranslation("dealModal");
  const isRemove = actionType === "remove";

  const rowOptions: SelectOption[] = options.map((opt) => ({
    ...opt,
    disabled: takenValues.includes(opt.value) && opt.value !== value,
  }));
  if (value && !rowOptions.some((opt) => opt.value === value)) {
    rowOptions.unshift({ label: value, value });
  }

  return (
    <div className="space-y-1">
      <label className={LABEL_CLASS}>{t("payoutPolicy.component")}</label>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <StyledSelect
            value={value}
            options={rowOptions}
            placeholder={t("payoutPolicy.selectComponents")}
            disabled={disabled}
            onChange={props.onChange}
          />
        </div>
        <button
          type="button"
          onClick={props.onAction}
          disabled={disabled || actionDisabled}
          aria-label={isRemove ? t("remove") : t("addComponent")}
          className={`size-8 shrink-0 rounded-full flex items-center justify-center disabled:opacity-50 transition ${isRemove ? "bg-[#FEE2E2] text-[#EF4444] hover:bg-[#FCA5A5]" : "bg-[#F4F4F5] text-[#71717A] hover:bg-[#E4E4E7]"}`}
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d={isRemove ? "M1 6H11" : "M6 1V11M1 6H11"} stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      {amount !== null && (
        <p className="text-xs font-semibold text-[#11181C] pl-1">
          {t("payoutPolicy.amount")}: {amount} {currencySymbol}
        </p>
      )}
    </div>
  );
};

type OutcomeCardProps = {
  row: OutcomeRegistryRow;
  baseAmount: number;
  currency: string;
  currencySymbol: string;
  allComponents: PayoutComponent[];
  policyValue: OutcomePolicyFormValue;
  updatePolicyValue: (patch: Partial<OutcomePolicyFormValue>) => void;
  componentOptions: Option[];
  tr: Translate;
};

const OutcomeCard: React.FC<OutcomeCardProps> = (props) => {
  const { row, baseAmount, currency, currencySymbol, allComponents, policyValue, updatePolicyValue, componentOptions, tr } = props;
  const { t } = useTranslation("dealModal");
  const [pendingComponent, setPendingComponent] = useState("");
  const selected = policyValue.selectedComponents || [];
  const pendingValue = selected.includes(pendingComponent) ? "" : pendingComponent;
  const baseDisabled = row.baseLocked || row.baseSelectable === false;
  const componentsDisabled = !row.componentsSelectable;

  const getAmount = (type: string) => getOutcomeComponentAmount(allComponents, type, baseAmount);
  const getLabel = (type: string) => componentOptions.find((opt) => opt.value === type)?.label ?? type;
  const setSelected = (next: string[]) => updatePolicyValue({ selectedComponents: next });

  const changeComponent = (index: number, value: string) => {
    if (value && !selected.includes(value)) setSelected(selected.map((item, i) => (i === index ? value : item)));
  };

  const availableOptions = componentOptions.filter((opt) => !selected.includes(opt.value));
  const canAddMore = !componentsDisabled && availableOptions.length > 0;

  const handleSelectPending = (value: string) => {
    if (!value) return;
    if (!selected.includes(value)) {
      setSelected([...selected, value]);
    }
    setPendingComponent("");
  };

  const addComponent = () => {
    if (!pendingValue) return;
    if (!selected.includes(pendingValue)) {
      setSelected([...selected, pendingValue]);
    }
    setPendingComponent("");
  };

  const totalAmount = getOutcomeTotal(baseAmount, policyValue.baseIncluded, selected, allComponents);
  const totalLabel = getOutcomeTotalLabel(policyValue.baseIncluded, selected, getLabel, t("payoutPolicy.baseShort"));

  return (
    <div className="rounded-3xl border border-[#E4E4E7] bg-white p-4 sm:p-6 shadow-sm">
      <div className="mb-3">
        <h4 className="text-base font-bold text-[#11181C]">{row.label ? tr(row.label) : formatRegistryText(row.key)}</h4>
        <p className="text-xs text-[#71717A] mt-0.5">{row.group}</p>
      </div>

      <label className={`inline-flex items-center gap-2 text-xs font-medium mb-5 ${baseDisabled ? "text-gray-400" : "text-[#11181C] cursor-pointer"}`}>
        <input
          type="checkbox"
          checked={policyValue.baseIncluded}
          disabled={baseDisabled}
          onChange={(e) => updatePolicyValue({ baseIncluded: e.target.checked })}
          className="size-4 rounded-md border-[#E4E4E7] text-[#0080FF] focus:ring-[#0080FF] disabled:opacity-50 disabled:cursor-not-allowed"
        />
        {t("payoutPolicy.baseAmountIncluded")} ({baseAmount} {currency})
      </label>

      <div className="space-y-3">
        {selected.map((type, i) => (
          <ComponentRow
            key={`${type}-${i}`}
            value={type}
            amount={getAmount(type)}
            currencySymbol={currencySymbol}
            options={componentOptions}
            takenValues={selected}
            disabled={componentsDisabled}
            actionType="remove"
            onChange={(value) => changeComponent(i, value)}
            onAction={() => setSelected(selected.filter((_, index) => index !== i))}
          />
        ))}
        {canAddMore && (
          <ComponentRow
            value={pendingValue}
            amount={pendingValue ? getAmount(pendingValue) : null}
            currencySymbol={currencySymbol}
            options={componentOptions}
            takenValues={selected}
            disabled={false}
            actionType="add"
            actionDisabled={!pendingValue}
            onChange={handleSelectPending}
            onAction={addComponent}
          />
        )}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100">
        <p className={LABEL_CLASS}>{t("payoutPolicy.total")}</p>
        <div className="rounded-2xl bg-[#F4F4F5] px-4 py-3 flex items-center justify-between">
          <div className="text-sm font-medium text-[#11181C]">{totalLabel}</div>
          <div className="text-sm font-bold text-[#11181C]">
            {totalAmount} {currencySymbol}
          </div>
        </div>
      </div>
    </div>
  );
};

type PayoutOutcomeEditorProps = {
  outcomeRegistry: OutcomeRegistry;
  baseAmount: number;
  currency: string;
  allComponents: PayoutComponent[];
  getPolicyValue: (row: OutcomeRegistryRow) => OutcomePolicyFormValue;
  updatePolicyValue: (row: OutcomeRegistryRow, patch: Partial<OutcomePolicyFormValue>) => void;
  resolveRegistryOptions: (optionsFrom?: string) => Option[];
  tr: Translate;
};

export const PayoutOutcomeEditor: React.FC<PayoutOutcomeEditorProps> = (props) => {
  const { outcomeRegistry, baseAmount, currency, allComponents, getPolicyValue, updatePolicyValue, resolveRegistryOptions, tr } = props;
  const { t } = useTranslation("dealModal");
  const rows = getOutcomeRegistryRowsForUi(outcomeRegistry, tr);
  const currencySymbol = getCurrencySymbol(currency);

  if (rows.length === 0) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-6 text-center">
        <p className="text-xs font-medium text-gray-400">{t("payoutPolicy.noPayoutPoliciesConfigured")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {rows.map((row) => (
        <OutcomeCard
          key={`${row.group}-${row.key}`}
          row={row}
          baseAmount={baseAmount}
          currency={currency}
          currencySymbol={currencySymbol}
          allComponents={allComponents}
          policyValue={getPolicyValue(row)}
          updatePolicyValue={(patch) => updatePolicyValue(row, patch)}
          componentOptions={resolveRegistryOptions(row.optionsFrom).map((opt) => ({ label: opt.label, value: String(opt.value) }))}
          tr={tr}
        />
      ))}
    </div>
  );
};